'use client';

import { useState } from 'react';
import { cn } from '../_lib/utils';
import type { SanityImage } from '../types';
import { Container, type ContainerProps } from '../Container';
import { Media } from '../Media';
import { Text } from '../Text';
import { VideoPlayer, type VideoPlayerProps } from '../VideoPlayer';

export type VideoProps = {
	autoplayVideo?: string;
	buttonSize?: VideoPlayerProps['buttonSize'];
	caption?: string;
	className?: string;
	image?: SanityImage;
	mediaAspectRatio?: 'default' | '16/9' | '5/4' | '4/3' | '1/1';
	nestedPopup?: boolean;
	parallax?: boolean;
	popup?: boolean;
	size?: ContainerProps['size'];
	video?: string;
	priority?: boolean;
	objectFit?: 'contain' | 'cover';
};

export function Video(props: VideoProps) {
	const [pauseVideo, setPauseVideo] = useState(props.autoplayVideo ? true : false);
	const mediaAspectRatio = props.mediaAspectRatio ?? 'default';
	const popup = props.popup ?? true;
	const size = props.size ?? 'lg';

	const image: SanityImage =
		props.image ??
		({
			_type: 'ImageDefault',
			asset: {
				_ref: '',
				url: `https://image.mux.com/${props.video}/thumbnail.webp`,
				altText: '',
				metadata: '',
			},
		} as SanityImage);

	return (
		<Container as='figure' className={cn('relative w-full', props.className)} size={getContainerSize(size)} data-component='Video'>
			{(image || props.autoplayVideo) && (
				<div
					className={cn(size !== 'unset' ? 'relative rounded-lg overflow-hidden' : '', mediaAspectRatio === '16/9' ? 'aspect-video' : '')}
					style={mediaAspectRatio !== '16/9' ? { aspectRatio: mediaAspectRatio } : undefined}
				>
					<Media
						className={`h-full [&_img]:h-full`}
						image={image}
						video={props.autoplayVideo}
						pauseVideo={pauseVideo}
						setPauseVideo={setPauseVideo}
						priority={props.priority}
						aspectRatio={mediaAspectRatio}
						objectFit={props.objectFit ?? 'contain'}
					/>
					{props.video && (
						<VideoPlayer
							buttonSize={props.buttonSize}
							className={`absolute! top-0 left-0 h-full w-full`}
							autoplay={false}
							nestedPopup={props.nestedPopup}
							popup={popup}
							video={props.video}
							setPauseVideo={setPauseVideo}
							preload={props.priority}
						/>
					)}
				</div>
			)}
			{props.caption && (
				<Text as='figcaption' className={`mt-5 mb-2`} styleType='body-sm'>
					{props.caption}
				</Text>
			)}
		</Container>
	);
}

function getContainerSize(size: VideoProps['size']) {
	switch (size) {
		case 'md':
			return 'lg';
		case 'sm':
			return 'md';
		default:
			return size;
	}
}
